/**
 * Import v2 상태 관리 (useReducer)
 *
 * 단일 모달 내부의 스텝 전환, 프리셋/파일/매핑/검증/결과 상태를 관리한다.
 * v2.3 기준: 스텝바 제거, 템플릿 스키마 동적 로드, 편집 모드 UX 지원.
 */

// v2.5: Figma 싱크 — 프리셋 힌트 인포박스 제거로 hintKey 삭제.
// steps 배열은 내부 흐름 제어용 (UI 미노출)이므로 한국어 유지.
import { isTotpCreateAllowed } from 'utils/plan'
import { normalizeHeader } from './templateForm'

export const PRESETS = [
  {
    id: 'officepassword',
    ext: '.json',
    // OfficePassword는 평문 .json과 암호화 .json.enc 모두 동일 파서(parseSafePasswordFile)가 처리.
    acceptedExts: ['.json', '.json.enc'],
    steps: ['유효성 검증', 'Import 완료'],
    labelKey: 'record.import.v2.presets.officepassword.label',
    descKey: 'record.import.v2.presets.officepassword.desc',
  },
  // 템플릿 양식(spec D1): 기본 템플릿 양식 CSV 를 내려받아 채워 올린다. 기본 선택(PRESETS[0])은 그대로 officepassword
  {
    id: 'template',
    ext: '.csv',
    acceptedExts: ['.csv'],
    steps: ['컬럼 매핑', '유효성 검증', 'Import 완료'],
    labelKey: 'record.import.v2.presets.template.label',
    descKey: 'record.import.v2.presets.template.desc',
    // 엑셀에서 만든 표를 올리는 카드 — .xlsx 를 올리면 alert 한 줄뿐이라, 파일을 고르기 전에 형식과 저장법을 알린다
    allowedFormatKey: 'record.import.v2.allowedFormatCsv',
  },
  {
    id: 'google',
    ext: '.csv',
    acceptedExts: ['.csv'],
    steps: ['컬럼 매핑', '유효성 검증', 'Import 완료'],
    labelKey: 'record.import.v2.presets.google.label',
    descKey: 'record.import.v2.presets.google.desc',
  },
  {
    id: 'custom',
    ext: '.csv',
    acceptedExts: ['.csv'],
    steps: ['컬럼 매핑', '유효성 검증', 'Import 완료'],
    labelKey: 'record.import.v2.presets.custom.label',
    descKey: 'record.import.v2.presets.custom.desc',
    // 엑셀에 직접 정리한 표가 가는 카드라 템플릿 카드와 같은 안내를 쓴다(구글 카드는 구글 비밀번호 관리자가 내보낸 파일을 받아 제외)
    allowedFormatKey: 'record.import.v2.allowedFormatCsv',
  },
  // PR7(#1514): Google Authenticator 이전 QR — 매핑 스텝 없음(officepassword 형).
  // 노출은 isTotpCreateAllowed 조건(ImportUploadStep 프리셋 필터).
  {
    id: 'googleauth',
    ext: '.png',
    acceptedExts: ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    steps: ['유효성 검증', 'Import 완료'],
    labelKey: 'record.import.v2.presets.googleauth.label',
    descKey: 'record.import.v2.presets.googleauth.desc',
  },
]

// v2.4: 필드 타입 ID를 영문 코드로 분리 (typeTranslation.js 패턴 준용).
// API/비교용 internal ID는 영문, UI 라벨은 labelKey로 번역.
export const FIELD_TYPES = {
  TEXT: 'text',
  SECURE_TEXT: 'secure_text',
  EMAIL: 'email',
  SECURE_EMAIL: 'secure_email',
  PASSWORD: 'password',
  PIN: 'pin',
  URL: 'url',
  NOTE: 'note',
  SECURE_NOTE: 'secure_note',
  DATE: 'date',
  // PR10(#1541): 타사 CSV 의 인증 키 컬럼을 정식 totp 필드로 수용
  TOTP: 'totp',
}

export const FIELD_TYPE_CONFIG = {
  [FIELD_TYPES.TEXT]: {
    itemType: 'string',
    encrypt: false,
    labelKey: 'record.import.v2.fieldType.text',
  },
  [FIELD_TYPES.SECURE_TEXT]: {
    itemType: 'string',
    encrypt: true,
    labelKey: 'record.import.v2.fieldType.secureText',
  },
  [FIELD_TYPES.EMAIL]: {
    itemType: 'email',
    encrypt: false,
    labelKey: 'record.import.v2.fieldType.email',
  },
  [FIELD_TYPES.SECURE_EMAIL]: {
    itemType: 'email',
    encrypt: true,
    labelKey: 'record.import.v2.fieldType.secureEmail',
  },
  [FIELD_TYPES.PASSWORD]: {
    itemType: 'password',
    encrypt: true,
    labelKey: 'record.import.v2.fieldType.password',
  },
  [FIELD_TYPES.PIN]: {
    itemType: 'password',
    encrypt: true,
    labelKey: 'record.import.v2.fieldType.pin',
  },
  [FIELD_TYPES.URL]: {
    itemType: 'url',
    encrypt: false,
    labelKey: 'record.import.v2.fieldType.url',
  },
  [FIELD_TYPES.NOTE]: {
    itemType: 'note',
    encrypt: false,
    labelKey: 'record.import.v2.fieldType.note',
  },
  [FIELD_TYPES.SECURE_NOTE]: {
    itemType: 'note',
    encrypt: true,
    labelKey: 'record.import.v2.fieldType.secureNote',
  },
  // v2.4: itemType을 'date'로 유지해 validator가 label 매칭 없이 날짜 필드를 판별할 수 있도록 한다.
  [FIELD_TYPES.DATE]: {
    itemType: 'date',
    encrypt: false,
    labelKey: 'record.import.v2.fieldType.date',
  },
  [FIELD_TYPES.TOTP]: {
    itemType: 'totp',
    encrypt: true,
    labelKey: 'record.import.v2.fieldType.totp',
  },
}

// 번역 함수와 결합해 드롭다운/매핑 UI에서 사용할 옵션 배열을 생성한다.
// i18next-extract는 dynamic key 평가 불가 → 스킵. record.import.v2.fieldType.* 키는 번역 JSON에 수동 관리.
export const getFieldTypeOptions = (t) =>
  Object.entries(FIELD_TYPE_CONFIG)
    // totp 는 isTotpCreateAllowed 게이팅(가져오기 = 생성 진입점 — flag OFF 또는 Basic 등급 시 드롭다운 미노출)
    .filter(([id]) => id !== FIELD_TYPES.TOTP || isTotpCreateAllowed())
    .map(([id, config]) => ({
      value: id,
      /* i18next-extract-disable-next-line */
      label: t(config.labelKey),
      itemType: config.itemType,
      encrypt: config.encrypt,
    }))

// "가져오지 않음"의 내부 식별자 (빈 문자열로 통일). UI 표시는 t('record.import.v2.mapping.doNotImport').
export const DO_NOT_IMPORT = ''

// v2.4: 제목 필드의 내부 식별자. validator가 field에 이 값을 저장하면 UI에서 t()로 번역 표시.
// applyFixes와 downloadExcludedCsv도 이 식별자로 비교한다.
export const TITLE_FIELD = '__title__'

export const initialState = {
  // 모달 생명주기 (스텝바 제거 후에도 내부 흐름 제어는 유지)
  phase: 'upload',
  step: 0,

  // 프리셋 & 파일
  preset: PRESETS[0],
  file: null,
  fileName: null,

  // 템플릿 스키마 (프리셋별 캐시)
  // { [presetId]: { status: 'idle'|'loading'|'success'|'error', schema?, error? } }
  templateSchemas: {},

  // 파싱 결과
  parsedData: null,
  isEncrypted: false,
  decryptPassword: null,

  // 컬럼 매핑
  mappings: [],
  nextFieldId: 100,

  // 유효성 검증 (action: null | 'editing' | 'fixed' | 'exclude')
  validationErrors: [],
  validRecords: [],
  totalRows: 0,

  // Import 결과
  importResult: null,
  isImporting: false,

  // googleauth(PR7): 다중 QR 누적 — [{ uri, batch }]. RESET/프리셋 전환 시 초기화.
  scannedQrs: [],

  // 템플릿 양식 프리셋(spec D10) — 카탈로그는 모달 세션 동안 캐시한다(프리셋 전환에서 유지, RESET 에서 비움)
  templateCatalog: { status: 'idle', items: [], dataTypes: [] },
  selectedTemplateId: null,
  // 'matched' | 'fallback' | null — 파일 헤더 자동 판별 결과 안내용(spec D6)
  templateDetection: null,
  // 매핑을 만든 템플릿 — 실행은 선택이 아니라 이 값으로 한다(spec D9)
  mappingTemplateId: null,

  // 파일 읽기·복호화 중 닫기·조작 잠금 — 늦게 끝난 작업이 바뀐 프리셋·새 세션 위에 결과를 쏟지 않게 한다.
  // RESET 이 initialState 로 되돌리므로 창을 닫으면 잠금이 남지 않는다
  isParsing: false,
}

// 커스텀 필드 매핑 행 — '필드 추가'와 '미연결 열을 필드로 추가'가 같은 모양을 쓴다
const createCustomMappingField = (id, fieldType, overrides = {}) => ({
  id,
  uid: null,
  // 기본 field는 type ID 그대로. UI에서 customLabel 또는 t(FIELD_TYPE_CONFIG[type].labelKey)로 표시
  field: fieldType,
  fieldLabelEn: null,
  customLabel: null,
  col: '',
  required: false,
  type: fieldType,
  locked: false,
  isCustom: true,
  isEditingLabel: false,
  labelDraft: '',
  ...overrides,
})

// 열 이름을 라벨로 쓰되 기존 매핑의 라벨(field·customLabel)과 겹치면 "이름 (2)", "(3)" … 으로 바꾼다. 라벨이 같으면
// 검증 화면의 수정값이 applyFixes 의 라벨 매칭으로 엉뚱한 필드에 들어간다(spec D14). 비교 규칙은 헤더 정규화와 같다
const getUniqueFieldLabel = (columnName, mappings) => {
  const usedLabels = new Set(
    mappings
      .flatMap((mapping) => [mapping.field, mapping.customLabel])
      .filter(Boolean)
      .map(normalizeHeader),
  )
  let label = columnName
  let suffix = 2
  while (usedLabels.has(normalizeHeader(label))) {
    label = `${columnName} (${suffix})`
    suffix += 1
  }
  return label
}

const importReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PRESET':
      return {
        ...state,
        preset: action.payload,
        file: null,
        fileName: null,
        scannedQrs: [],
        selectedTemplateId: null,
        templateDetection: null,
        mappingTemplateId: null,
      }

    // googleauth(PR7): 스캔된 QR 추가 — 같은 장 재업로드/다른 batch_id 정책은
    // 호출측(GoogleAuthUploadPanel)이 판정하고 여기는 순수 누적만 담당.
    case 'ADD_SCANNED_QR':
      return { ...state, scannedQrs: [...state.scannedQrs, action.payload] }

    // 선택·판별은 비운다 — 판별은 비동기라 그 사이 이전 파일의 선택으로 진행하지 않게 한다(spec D10)
    case 'SET_FILE':
      return {
        ...state,
        file: action.payload.file,
        fileName: action.payload.fileName,
        selectedTemplateId: null,
        templateDetection: null,
      }

    case 'SET_PARSED_DATA':
      return {
        ...state,
        parsedData: action.payload.parsedData,
        isEncrypted: action.payload.isEncrypted || false,
      }

    case 'SET_DECRYPT_PASSWORD':
      return { ...state, decryptPassword: action.payload }

    // 템플릿 스키마 로드 (프리셋 선택 시 백그라운드 prefetch)
    case 'LOAD_TEMPLATE_START':
      return {
        ...state,
        templateSchemas: {
          ...state.templateSchemas,
          [action.payload.presetId]: { status: 'loading' },
        },
      }

    case 'LOAD_TEMPLATE_SUCCESS':
      return {
        ...state,
        templateSchemas: {
          ...state.templateSchemas,
          [action.payload.presetId]: { status: 'success', schema: action.payload.schema },
        },
      }

    case 'LOAD_TEMPLATE_ERROR':
      return {
        ...state,
        templateSchemas: {
          ...state.templateSchemas,
          [action.payload.presetId]: { status: 'error', error: action.payload.error },
        },
      }

    case 'LOAD_CATALOG_START':
      return { ...state, templateCatalog: { status: 'loading', items: [], dataTypes: [] } }

    case 'LOAD_CATALOG_SUCCESS':
      return {
        ...state,
        templateCatalog: {
          status: 'success',
          items: action.payload.items,
          dataTypes: action.payload.dataTypes,
        },
      }

    case 'LOAD_CATALOG_ERROR':
      return { ...state, templateCatalog: { status: 'error', items: [], dataTypes: [] } }

    case 'SELECT_TEMPLATE':
      return {
        ...state,
        selectedTemplateId: action.payload.id,
        templateDetection: action.payload.detection ?? null,
      }

    case 'SET_MAPPING_TEMPLATE':
      return { ...state, mappingTemplateId: action.payload }

    case 'GO_NEXT':
      if (state.phase === 'upload') {
        return { ...state, phase: 'steps', step: 0 }
      }
      return { ...state, step: state.step + 1 }

    case 'GO_PREV':
      if (state.step === 0) {
        return { ...state, phase: 'upload' }
      }
      return { ...state, step: state.step - 1 }

    case 'INIT_MAPPINGS':
      return { ...state, mappings: action.payload }

    case 'UPDATE_MAPPING_COL':
      return {
        ...state,
        mappings: state.mappings.map((m) =>
          m.id === action.payload.id ? { ...m, col: action.payload.col } : m,
        ),
      }

    case 'UPDATE_MAPPING_TYPE':
      return {
        ...state,
        mappings: state.mappings.map((m) =>
          m.id === action.payload.id ? { ...m, type: action.payload.type } : m,
        ),
      }

    // 커스텀 필드 라벨 편집: 편집 모드 진입 (labelDraft에 현재 라벨 복사)
    case 'START_EDIT_CUSTOM_LABEL':
      // payload.defaultLabel — 컴포넌트가 t(FIELD_TYPE_CONFIG[type].labelKey)로 번역해 전달.
      // 미전달 시 customLabel 또는 type ID를 폴백으로 사용.
      return {
        ...state,
        mappings: state.mappings.map((m) =>
          m.id === action.payload.id
            ? {
                ...m,
                isEditingLabel: true,
                labelDraft: m.customLabel || action.payload.defaultLabel || m.type,
              }
            : m,
        ),
      }

    case 'UPDATE_CUSTOM_LABEL_DRAFT':
      return {
        ...state,
        mappings: state.mappings.map((m) =>
          m.id === action.payload.id ? { ...m, labelDraft: action.payload.value } : m,
        ),
      }

    // 커스텀 필드 라벨 저장 (✓ 버튼 클릭)
    case 'COMMIT_CUSTOM_LABEL':
      return {
        ...state,
        mappings: state.mappings.map((m) => {
          if (m.id !== action.payload.id) return m
          const trimmed = (m.labelDraft || '').trim()
          if (!trimmed) return m
          return { ...m, customLabel: trimmed, isEditingLabel: false, labelDraft: '' }
        }),
      }

    case 'ADD_MAPPING_FIELD': {
      // v2.3: payload.type으로 지정된 타입의 행 추가 (v2.4: 영문 ID 기준)
      const fieldType = action.payload?.type || FIELD_TYPES.TEXT
      const newField = createCustomMappingField(state.nextFieldId, fieldType)
      return {
        ...state,
        mappings: [...state.mappings, newField],
        nextFieldId: state.nextFieldId + 1,
      }
    }

    // 미연결 열을 고른 타입의 커스텀 필드로 추가한다(spec D14). 타입을 먼저 고르게 하는 이유: 추가된 행의 타입을
    // 바꾸는 UI 가 없다 — UPDATE_MAPPING_TYPE 은 이 파일 밖에 호출처가 없다. 텍스트로 넣으면 비밀번호 열이 평문
    // 필드로 저장된다
    case 'ADD_MAPPING_FIELD_FOR_COLUMN': {
      const { col, type: fieldType } = action.payload
      const newField = createCustomMappingField(state.nextFieldId, fieldType, {
        col,
        customLabel: getUniqueFieldLabel(col, state.mappings),
      })
      return {
        ...state,
        mappings: [...state.mappings, newField],
        nextFieldId: state.nextFieldId + 1,
      }
    }

    case 'REMOVE_MAPPING_FIELD':
      return {
        ...state,
        mappings: state.mappings.filter((m) => m.id !== action.payload),
      }

    case 'SET_VALIDATION_RESULT':
      return {
        ...state,
        validationErrors: action.payload.errors,
        validRecords: action.payload.validRecords,
        totalRows: action.payload.totalRows,
      }

    // 수정 버튼 클릭: 편집 모드 진입
    case 'SET_ERROR_EDITING':
      return {
        ...state,
        validationErrors: state.validationErrors.map((e) =>
          e.id === action.payload.id ? { ...e, action: 'editing', fix: '', editError: null } : e,
        ),
      }

    case 'SET_ERROR_FIX':
      return {
        ...state,
        validationErrors: state.validationErrors.map((e) =>
          e.id === action.payload.id ? { ...e, fix: action.payload.fix, editError: null } : e,
        ),
      }

    // 저장 시 유효성 검증 실패 (편집 유지)
    case 'SET_EDIT_ERROR':
      return {
        ...state,
        validationErrors: state.validationErrors.map((e) =>
          e.id === action.payload.id ? { ...e, editError: action.payload.message } : e,
        ),
      }

    // ✓ 저장: 검증 통과 후 fixed 상태로 전이
    case 'COMMIT_ERROR_FIX':
      return {
        ...state,
        validationErrors: state.validationErrors.map((e) =>
          e.id === action.payload.id ? { ...e, action: 'fixed', editError: null } : e,
        ),
      }

    // 제외 버튼 클릭: exclude 상태로 전이
    case 'SET_ERROR_EXCLUDE':
      return {
        ...state,
        validationErrors: state.validationErrors.map((e) =>
          e.id === action.payload.id ? { ...e, action: 'exclude', fix: '', editError: null } : e,
        ),
      }

    // 취소: 대기 상태로 복귀 (수정/제외 버튼 다시 표시)
    case 'CANCEL_ERROR_ACTION':
      return {
        ...state,
        validationErrors: state.validationErrors.map((e) => {
          if (e.id !== action.payload.id) return e
          // PR7(#1514) 중복 경고 행: pending 으로 돌리면 '수정' 없이는 import 버튼이
          // 잠겨 "그대로 포함" 경로가 없다(PR #1518 리뷰 major). 취소를 제외↔포함
          // 토글로 처리 — 포함은 원제목(e.value) 그대로 fixed(applyFixes 가 동일 title 재기록).
          if (e.reason === 'duplicateTotp') {
            return e.action === 'exclude'
              ? { ...e, action: 'fixed', fix: e.value, editError: null }
              : { ...e, action: 'exclude', fix: '', editError: null }
          }
          return { ...e, action: null, fix: '', editError: null }
        }),
      }

    case 'SET_IMPORTING':
      return { ...state, isImporting: action.payload }

    case 'SET_PARSING':
      return { ...state, isParsing: action.payload }

    case 'SET_IMPORT_RESULT':
      return { ...state, importResult: action.payload }

    case 'RESET':
      return { ...initialState }

    default:
      return state
  }
}

export default importReducer
